import { useEffect } from 'react';
import { api } from './endpoints.js';

/** Until the server says otherwise in its first answer. */
const DEFAULT_BEAT_SECONDS = 25;

/**
 * The beat that puts this phone in the admin panel's live count of who is in
 * the app (server: modules/presence). Nothing of it shows in the app itself.
 *
 * Mounted once for the whole session, so whoever is on the mine, the wallet or
 * the tasks counts too — not only whoever is on Home. It beats while the app is
 * in front of the person and stops while it is in the background, so a phone
 * with the app left open in a drawer drops out of the count a minute later.
 * Coming back to the app beats at once.
 */
export function usePresenceBeat(): void {
  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    let everyMs = DEFAULT_BEAT_SECONDS * 1000;

    const beat = async () => {
      if (stopped || document.visibilityState !== 'visible') return;
      try {
        const answer = await api.presence.beat();
        if (answer.beatSeconds > 0) everyMs = answer.beatSeconds * 1000;
      } catch {
        // A lost beat costs nothing: the next one is seconds away, and the
        // server keeps an account counted for three beats.
      }
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void beat().finally(() => {
          if (!stopped) schedule();
        });
      }, everyMs);
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void beat();
      schedule();
    };

    void beat();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
